import type { ConnState } from "../net/hub";

/**
 * WHAT THE PLAYER SEES WHEN THE CONNECTION DROPS.
 *
 * 🚨 **BEFORE 2026-08-22 THE ANSWER WAS: NOTHING.** `net/hub.ts` logged `[hub] closed` to the
 * console and `ui/` had no component that could say otherwise — `report`, `tip`, `settings`,
 * `cardZoom`, `page` and `betamark` was the whole set. `withAutomaticReconnect` retries for about
 * seventeen seconds before giving up, so a player whose wifi hiccupped watched a board that had
 * simply stopped responding, with no way to tell a network drop from a frozen game.
 *
 * ⛔ **THE ORIGINAL CLIENT CARRIED TWO VIEWS FOR THIS STATE** — `ReconnectionView` and
 * `NetworkIssuesView` — so the absence was a REGRESSION we shipped, not a design decision anyone
 * made. `docs/SCREEN-COVERAGE.md` records it as the one confirmed gap of the four candidates.
 *
 * ⭐ **IT SAYS WHAT IS HAPPENING AND WHAT THE PLAYER SHOULD DO**, which is the whole difference
 * between a notice and decoration. "Reconnecting" during the retry window is reassurance and asks
 * for nothing; "Connection lost" afterwards has to say that reloading is safe, because the fear at
 * that moment is losing the match — and the server owns the match state, so it genuinely is.
 *
 * ⛔ **IT MOUNTS ON `document.body`, NOT ON THE STAGE**, and uses the `--chrome-*` REAL screen
 * pixels rather than a ladder rung. `styles.css` is explicit about this: the ladder is generated
 * from `RevivalUIStyle.SizeFor` and every rung is stage-relative by construction, so anything
 * `position: fixed` that borrows one renders at the wrong size. Eight declarations across `.tip*`,
 * `.settings*` and `.betamark*` made exactly that mistake, including the tooltip Alex twice
 * reported as unreadable.
 *
 * ⚠️ **A RECONNECT MUST CLEAR IT.** A notice that can appear and not disappear is worse than none:
 * it teaches the player to ignore the one place the game talks to them.
 */
let root: HTMLElement | null = null;

function ensure(): HTMLElement {
  if (root) return root;
  const el = document.createElement("div");
  el.className = "connstate";
  // Announced, not just drawn: a player using a screen reader gets the same news at the same time.
  el.setAttribute("role", "status");
  el.setAttribute("aria-live", "polite");
  document.body.append(el);
  root = el;
  return el;
}

const COPY: Record<Exclude<ConnState, "connected">, { title: string; note: string }> = {
  reconnecting: {
    title: "Reconnecting",
    note: "The connection dropped. Trying to pick the game back up.",
  },
  lost: {
    title: "Connection lost",
    /* ⚠️ NO EM DASH. Alex's standing rule covers ALL copy, and player-facing copy most of all. */
    note: "Reload to rejoin. The match is held on the server, not in this tab.",
  },
};

/** Draw the connection state. `connected` clears it. */
export function showConnState(state: ConnState): void {
  if (state === "connected") {
    root?.remove();
    root = null;
    return;
  }
  const el = ensure();
  el.dataset["state"] = state;
  const { title, note } = COPY[state];
  el.replaceChildren();
  const h = document.createElement("b");
  h.className = "connstate__title";
  h.textContent = title;
  const p = document.createElement("span");
  p.className = "connstate__note";
  p.textContent = note;
  el.append(h, p);
}
